<template>
  <div class="weekly-hours">
    <p class="weekly-hours__hint">
      {{ mobile ? $t("weekly_schedule_hint_touch") : $t("weekly_schedule_hint") }}
    </p>

    <div class="weekly-hours__day-tabs">
      <button
        v-for="weekday in WEEKDAYS"
        :key="weekday"
        type="button"
        class="weekly-hours__day-tab"
        :class="{ 'weekly-hours__day-tab--active': weekday === selectedWeekday }"
        @click="selectedWeekday = weekday"
      >
        <span class="weekly-hours__day-tab-name">{{ dayLabel(weekday) }}</span>
        <span class="weekly-hours__day-tab-summary">{{ summaryFor(weekday) }}</span>
      </button>
    </div>

    <div class="weekly-hours__toolbar">
      <VBtn size="small" variant="text" color="error" @click="confirmClearDay">
        {{ $t("clear_day") }}
      </VBtn>
      <VBtn size="small" variant="text" color="#403838" @click="copyToAllDays">
        {{ $t("copy_to_all_days") }}
      </VBtn>
    </div>

    <div ref="gridRef" class="weekly-hours__grid">
      <button
        v-for="cell in cellsForSelectedDay"
        :key="cell.minutes"
        type="button"
        :data-minutes="cell.minutes"
        class="weekly-hours__cell"
        :class="{
          'weekly-hours__cell--open': cell.open,
          'weekly-hours__cell--hour': cell.minutes % 60 === 0,
        }"
        @mousedown.prevent="startDrag(cell.minutes)"
        @mouseenter="dragOver(cell.minutes)"
        @mouseup="endDrag"
        @touchstart="onTouchStart(cell.minutes)"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd(cell.minutes)"
      >
        <span v-if="cell.minutes % 60 === 0" class="weekly-hours__cell-time">
          {{ minutesToLabel(cell.minutes) }}
        </span>
      </button>
    </div>

    <DeleteDialog v-model="clearDayDialog" @delete="clearDay" />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useDisplay } from "vuetify";
import { useI18n } from "vue-i18n";
import { WorkingHoursSchedule } from "@/main/types/working-hours-schedule.type";
import DeleteDialog from "@/main/components/delete-popup.vue";

const props = defineProps<{ schedules?: WorkingHoursSchedule[] }>();

const { mobile } = useDisplay();
const { t } = useI18n();

// Rails weekday convention (0 = Sunday .. 6 = Saturday), shown Monday-first.
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0];
const CELL_MINUTES = Array.from({ length: 96 }, (_, i) => i * 15);

const cellKey = (weekday: number, minutes: number) => `${weekday}:${minutes}`;

function toMinutes(hhmm: string) {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

function minutesToLabel(minutes: number) {
  const h = Math.floor(minutes / 60)
    .toString()
    .padStart(2, "0");
  const m = (minutes % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
}

// Jan 7 2024 was a Sunday - offsetting from it gives every Rails weekday
// number (0-6) a real date to format a locale-aware short day name from.
const dayLabel = (weekday: number) =>
  new Date(2024, 0, 7 + weekday).toLocaleDateString(undefined, { weekday: "short" });

const openSet = ref(new Set<string>());
const originalIds = ref<number[]>([]);
const selectedWeekday = ref(1);

function loadSchedules(schedules?: WorkingHoursSchedule[]) {
  const set = new Set<string>();
  const ids: number[] = [];

  (schedules ?? []).forEach((s) => {
    if (
      s._destroy === 1 ||
      !s.opens_at ||
      !s.closes_at ||
      s.weekday === undefined ||
      s.weekday === null
    ) {
      return;
    }
    if (typeof s.id === "number") ids.push(s.id);

    const start = toMinutes(s.opens_at);
    const end = toMinutes(s.closes_at);
    for (let m = start; m < end; m += 15) {
      set.add(cellKey(s.weekday, m));
    }
  });

  openSet.value = set;
  originalIds.value = ids;
}

loadSchedules(props.schedules);

const cellsForSelectedDay = computed(() =>
  CELL_MINUTES.map((minutes) => ({
    minutes,
    open: openSet.value.has(cellKey(selectedWeekday.value, minutes)),
  }))
);

// Contiguous runs of open 15-minute cells for a day become one schedule row
// each - a gap between runs is a break, and needs no special representation
// of its own.
function deriveRangesForWeekday(weekday: number) {
  const openMinutes = CELL_MINUTES.filter((m) =>
    openSet.value.has(cellKey(weekday, m))
  ).sort((a, b) => a - b);

  const ranges: { opens_at: string; closes_at: string }[] = [];
  let rangeStart: number | null = null;
  let prev: number | null = null;

  openMinutes.forEach((m) => {
    if (rangeStart === null) {
      rangeStart = m;
    } else if (prev !== null && m - prev > 15) {
      ranges.push({ opens_at: minutesToLabel(rangeStart), closes_at: minutesToLabel(prev + 15) });
      rangeStart = m;
    }
    prev = m;
  });
  if (rangeStart !== null && prev !== null) {
    ranges.push({ opens_at: minutesToLabel(rangeStart), closes_at: minutesToLabel(prev + 15) });
  }

  return ranges;
}

const summaryFor = (weekday: number) => {
  const ranges = deriveRangesForWeekday(weekday);
  if (!ranges.length) return t("closed");
  return ranges.map((r) => `${r.opens_at}–${r.closes_at}`).join(", ");
};

function setCell(weekday: number, minutes: number, open: boolean) {
  const key = cellKey(weekday, minutes);
  if (open) openSet.value.add(key);
  else openSet.value.delete(key);
}

// Mouse drag-to-paint: the first cell touched decides whether the drag
// opens or closes cells, so dragging over already-open hours closes them
// (that's how you carve out a break) and dragging over closed hours opens
// them.
const dragTargetOpen = ref<boolean | null>(null);

const startDrag = (minutes: number) => {
  const targetOpen = !openSet.value.has(cellKey(selectedWeekday.value, minutes));
  dragTargetOpen.value = targetOpen;
  setCell(selectedWeekday.value, minutes, targetOpen);
};

const dragOver = (minutes: number) => {
  if (dragTargetOpen.value === null) return;
  setCell(selectedWeekday.value, minutes, dragTargetOpen.value);
};

const endDrag = () => {
  dragTargetOpen.value = null;
};

// Touch: a quick tap toggles one cell; press-and-hold-then-drag paints a
// range, same as the mouse behavior - this keeps a plain scroll gesture
// (which also starts with a touch on a cell) from being hijacked before the
// user has shown they actually mean to drag.
let longPressTimer: number | null = null;
let touchDragging = false;

const clearLongPressTimer = () => {
  if (longPressTimer !== null) {
    window.clearTimeout(longPressTimer);
    longPressTimer = null;
  }
};

const onTouchStart = (minutes: number) => {
  touchDragging = false;
  clearLongPressTimer();
  longPressTimer = window.setTimeout(() => {
    touchDragging = true;
    startDrag(minutes);
  }, 350);
};

const onTouchMove = (event: TouchEvent) => {
  if (!touchDragging) {
    clearLongPressTimer();
    return;
  }
  event.preventDefault();
  const touch = event.touches[0];
  const el = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement | null;
  const cellEl = el?.closest<HTMLElement>("[data-minutes]");
  if (cellEl) dragOver(Number(cellEl.dataset.minutes));
};

const onTouchEnd = (minutes: number) => {
  clearLongPressTimer();
  if (touchDragging) {
    endDrag();
  } else {
    setCell(selectedWeekday.value, minutes, !openSet.value.has(cellKey(selectedWeekday.value, minutes)));
  }
  touchDragging = false;
};

const onGlobalTouchEnd = () => {
  clearLongPressTimer();
  touchDragging = false;
  endDrag();
};

const clearDayDialog = ref(false);

const confirmClearDay = () => {
  clearDayDialog.value = true;
};

const clearDay = () => {
  CELL_MINUTES.forEach((m) => openSet.value.delete(cellKey(selectedWeekday.value, m)));
};

const copyToAllDays = () => {
  const sourceMinutes = CELL_MINUTES.filter((m) =>
    openSet.value.has(cellKey(selectedWeekday.value, m))
  );
  WEEKDAYS.forEach((weekday) => {
    if (weekday === selectedWeekday.value) return;
    CELL_MINUTES.forEach((m) => openSet.value.delete(cellKey(weekday, m)));
    sourceMinutes.forEach((m) => openSet.value.add(cellKey(weekday, m)));
  });
};

// Full-replace save: destroy every row that existed on load and submit the
// current state as entirely new rows, rather than trying to diff edited
// ranges back onto their original ids - simpler and can't drift out of sync.
const getScheduleAttributes = (): WorkingHoursSchedule[] => {
  const destroyed: WorkingHoursSchedule[] = originalIds.value.map((id) => ({
    id,
    _destroy: 1,
  }));
  const created: WorkingHoursSchedule[] = WEEKDAYS.flatMap((weekday) =>
    deriveRangesForWeekday(weekday).map((range) => ({
      _destroy: 0,
      weekday,
      opens_at: range.opens_at,
      closes_at: range.closes_at,
    }))
  );
  return [...destroyed, ...created];
};

const resetFromSchedules = (schedules?: WorkingHoursSchedule[]) => {
  loadSchedules(schedules);
};

defineExpose({ getScheduleAttributes, resetFromSchedules });

const gridRef = ref<HTMLElement>();
let visibilityObserver: ResizeObserver | null = null;

onMounted(() => {
  window.addEventListener("mouseup", endDrag);
  window.addEventListener("touchend", onGlobalTouchEnd);
  window.addEventListener("touchcancel", onGlobalTouchEnd);

  // Scroll past the small hours so business hours are visible right away.
  // This settings tab is kept mounted but `v-show`-hidden while another tab
  // is active, so `scrollHeight` reads 0 until the user actually switches to
  // it - a ResizeObserver reliably catches that hidden-to-visible moment
  // (a fixed number of rAF retries at mount time would just give up first).
  let scrolled = false;
  visibilityObserver = new ResizeObserver(() => {
    const grid = gridRef.value;
    if (scrolled || !grid || !grid.scrollHeight) return;
    scrolled = true;
    const cellHeight = grid.scrollHeight / CELL_MINUTES.length;
    grid.scrollTop = cellHeight * (7 * 4);
    visibilityObserver?.disconnect();
  });
  if (gridRef.value) visibilityObserver.observe(gridRef.value);
});

onUnmounted(() => {
  window.removeEventListener("mouseup", endDrag);
  window.removeEventListener("touchend", onGlobalTouchEnd);
  window.removeEventListener("touchcancel", onGlobalTouchEnd);
  visibilityObserver?.disconnect();
});
</script>

<style lang="scss" scoped>
.weekly-hours {
  &__hint {
    color: rgba(0, 0, 0, 0.6);
    font-size: 13px;
    margin-bottom: 8px;
  }

  &__day-tabs {
    display: flex;
    gap: 6px;
    overflow-x: auto;
    padding: 2px 2px 8px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__day-tab {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    min-width: 96px;
    padding: 8px 12px;
    border: none;
    border-radius: 10px;
    background: #f3f4f6;
    cursor: pointer;

    &--active {
      background: #1976d2;

      .weekly-hours__day-tab-name,
      .weekly-hours__day-tab-summary {
        color: #fff;
      }
    }
  }

  &__day-tab-name {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    color: #6b7280;
  }

  &__day-tab-summary {
    font-size: 11px;
    color: #1f2937;
    white-space: nowrap;
  }

  &__toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 4px;
  }

  &__grid {
    max-height: min(55vh, 480px);
    overflow-y: auto;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    user-select: none;
    touch-action: pan-y;
  }

  &__cell {
    display: block;
    width: 100%;
    height: 18px;
    padding: 0 10px;
    border: none;
    border-top: 1px solid #f3f4f6;
    background: #fff;
    text-align: left;
    cursor: pointer;

    &--hour {
      border-top: 1px solid #e5e7eb;
      height: 26px;
    }

    &--open {
      background: #e8f5e9;

      &:hover {
        background: #c8e6c9;
      }
    }

    &:hover:not(&--open) {
      background: #f3f4f6;
    }
  }

  &__cell-time {
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
  }
}
</style>
